/**
 * LaunchAgentLogDrawer — slide-in side drawer that streams the last
 * ~200 lines of a chosen LaunchAgent's stdout/stderr (Phase batch 2,
 * #6). Polls /api/launchagent-log every 5s while open. Closes on Esc
 * or backdrop click.
 *
 * Mounted once globally from App.ts; opened via openLaunchAgentLog().
 */
import { html, render, type TemplateResult } from "lit-html";
import { api } from "../data/api";
import type { LaunchAgentLogTail } from "../data/types";

interface TriggerState {
  status:    "idle" | "queued" | "running" | "done" | "error";
  message:   string;
  triggerId: string | null;
  pollTimer: ReturnType<typeof setInterval> | null;
}

interface DrawerState {
  open:       boolean;
  label:      string | null;     // short label, e.g. "nba-ai-picker"
  data:       LaunchAgentLogTail | null;
  loading:    boolean;
  error:      string | null;
  pollTimer:  ReturnType<typeof setInterval> | null;
  trigger:    TriggerState;
  paint:      () => void;
}

const state: DrawerState = {
  open: false, label: null, data: null, loading: false,
  error: null, pollTimer: null,
  trigger: { status: "idle", message: "", triggerId: null, pollTimer: null },
  paint: () => {},
};

export function renderLaunchAgentLogDrawer(host: HTMLElement): void {
  state.paint = () => render(template(), host);
  state.paint();

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && state.open) close();
  });
}

export function openLaunchAgentLog(shortLabel: string): void {
  state.open = true;
  state.label = shortLabel;
  state.data = null;
  state.error = null;
  state.loading = true;
  resetTrigger();
  state.paint();
  fetchOnce();
  if (state.pollTimer) clearInterval(state.pollTimer);
  state.pollTimer = setInterval(fetchOnce, 5000);
}

function close(): void {
  state.open = false;
  if (state.pollTimer) { clearInterval(state.pollTimer); state.pollTimer = null; }
  if (state.trigger.pollTimer) { clearInterval(state.trigger.pollTimer); state.trigger.pollTimer = null; }
  state.paint();
}

function resetTrigger(): void {
  if (state.trigger.pollTimer) clearInterval(state.trigger.pollTimer);
  state.trigger = { status: "idle", message: "", triggerId: null, pollTimer: null };
}

async function startTrigger(): Promise<void> {
  if (!state.label) return;
  if (state.trigger.status === "queued" || state.trigger.status === "running") return;
  const fullLabel = `com.yurysankov.${state.label}`;
  state.trigger = { status: "queued", message: "queued — waiting for Mac reporter (≤60s)…",
                     triggerId: null, pollTimer: null };
  state.paint();
  try {
    const r = await api.launchagentTrigger(fullLabel);
    state.trigger.triggerId = r.id;
    state.trigger.status    = "queued";
    state.trigger.message   = `queued · id ${r.id.slice(-6)}`;
    state.paint();
    state.trigger.pollTimer = setInterval(pollTriggerResult, 3000);
  } catch (e) {
    state.trigger.status  = "error";
    state.trigger.message = e instanceof Error ? e.message : String(e);
    state.paint();
  }
}

async function pollTriggerResult(): Promise<void> {
  const id = state.trigger.triggerId;
  if (!id) return;
  try {
    const r = await api.launchagentTriggerStatus(id);
    if (r.status === "done" && r.result) {
      state.trigger.status  = r.result.ok ? "done" : "error";
      state.trigger.message = r.result.ok
        ? `kicked off · exit ${r.result.exit_code}`
        : `failed · exit ${r.result.exit_code}: ${r.result.stderr.slice(0, 200)}`;
      if (state.trigger.pollTimer) {
        clearInterval(state.trigger.pollTimer);
        state.trigger.pollTimer = null;
      }
      state.paint();
      // Force-refresh the log a couple seconds after kickstart so new lines show
      setTimeout(fetchOnce, 2000);
    }
  } catch {
    // Silent — keep polling, the drawer will show the previous state
  }
}

async function fetchOnce(): Promise<void> {
  if (!state.label) return;
  state.loading = state.data == null;     // only spin on first load
  state.paint();
  try {
    const r = await api.launchagentLog(state.label, 300);
    state.data = r;
    state.error = null;
    state.loading = false;
    state.paint();

    // Auto-scroll to bottom of pre on next frame so the freshest line is visible
    queueMicrotask(() => {
      const pre = document.getElementById("la-log-pre");
      if (pre) pre.scrollTop = pre.scrollHeight;
    });
  } catch (e) {
    state.loading = false;
    state.error = e instanceof Error ? e.message : String(e);
    state.paint();
  }
}

function template(): TemplateResult {
  if (!state.open) return html``;
  return html`
    <div class="la-log-backdrop" @click=${close}></div>
    <aside class="la-log-drawer" role="dialog" aria-label="LaunchAgent log">
      <header class="la-log-drawer__head">
        <div>
          <span class="la-log-drawer__eyebrow">launchagent log</span>
          <h2 class="la-log-drawer__title">com.yurysankov.${state.label ?? "?"}</h2>
          <span class="la-log-drawer__sub">
            ${state.data
              ? `${state.data.returned_lines} of ${state.data.total_lines} lines · ${formatBytes(state.data.bytes)} · refreshed ${formatRel(state.data.fetched_at)}`
              : state.loading ? "loading…" : ""}
          </span>
        </div>
        <div class="la-log-drawer__head-actions">
          ${triggerButton()}
          <button class="la-log-drawer__close" @click=${close}>✕</button>
        </div>
      </header>
      ${triggerStatusRow()}
      <div class="la-log-drawer__body">
        ${state.error ? html`
          <div class="la-log-drawer__error">
            <strong>error</strong> ${state.error}
          </div>
        ` : !state.data?.log ? html`
          <div class="la-log-drawer__empty">
            ${state.loading ? "fetching tail…" : "no log content (may be a fresh agent that hasn't fired yet)"}
          </div>
        ` : html`
          <pre id="la-log-pre" class="la-log-drawer__pre">${highlightLog(state.data.log)}</pre>
        `}
      </div>
    </aside>
  `;
}

function triggerButton(): TemplateResult {
  const t = state.trigger;
  const busy = t.status === "queued" || t.status === "running";
  return html`
    <button class="la-log-drawer__trigger ${busy ? "is-busy" : ""}"
            ?disabled=${busy}
            title="launchctl kickstart -k gui/$UID/com.yurysankov.${state.label ?? ""}"
            @click=${startTrigger}>
      ${busy ? "…" : "⚡"} trigger
    </button>
  `;
}

function triggerStatusRow(): TemplateResult | "" {
  const t = state.trigger;
  if (t.status === "idle") return "";
  const cls = t.status === "done"   ? "is-ok"
            : t.status === "error"  ? "is-err"
            : "is-pending";
  return html`
    <div class="la-log-drawer__trigger-status ${cls}">
      <span class="la-log-drawer__trigger-status-label">trigger:</span>
      <span>${t.message}</span>
    </div>
  `;
}

function highlightLog(text: string): TemplateResult[] {
  return text.split("\n").map((line) => {
    const cls = /error|fail|exception|traceback/i.test(line) ? "is-err"
              : /warn/i.test(line)                              ? "is-warn"
              : /success|done|ok|wrote/i.test(line)             ? "is-ok"
              : "";
    return html`<span class="la-log-drawer__line ${cls}">${line}</span>\n`;
  });
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRel(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (ms < 0) return "just now";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}
